import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback
} from 'react';
import {
  Restaurant,
  RestaurantCrmData,
  Activity,
  ActivityType,
  ActivityOutcome,
  CrmStatus,
  PerformanceFilters,
  OverviewKpis,
  MarketRow,
  UploadedList
} from './types';
import {
  RESTAURANTS,
  INITIAL_CRM_DATA,
  OVERVIEW_KPIS,
  MARKET_ROWS,
  MARKETS,
  BRANDS,
  TIME_PERIODS,
  INITIAL_LISTS
} from './fakeData';

const STORAGE_KEY = 'performance_crm_state';

// ─── State ───

interface PerformanceState {
  restaurants: Restaurant[];
  crmData: RestaurantCrmData[];
  filters: PerformanceFilters;
  kpis: OverviewKpis;
  marketRows: MarketRow[];
  lists: UploadedList[];
}

// ─── Actions ───

type Action =
  | {
      type: 'ADD_ACTIVITY';
      payload: {
        restaurant_id: number;
        activity: Omit<Activity, 'id' | 'created_at' | 'restaurant_id'>;
      };
    }
  | {
      type: 'UPDATE_CRM_STATUS';
      payload: { restaurant_id: number; status: CrmStatus };
    }
  | {
      type: 'BULK_UPDATE_STATUS';
      payload: { restaurant_ids: number[]; status: CrmStatus };
    }
  | { type: 'SET_FILTERS'; payload: Partial<PerformanceFilters> }
  | { type: 'HYDRATE'; payload: PerformanceState }
  | { type: 'ADD_RESTAURANT'; payload: { restaurant: Restaurant } }
  | {
      type: 'ADD_LIST';
      payload: { list: UploadedList; newRestaurants: Restaurant[] };
    }
  | { type: 'DELETE_LIST'; payload: { listId: string } };

function generateId(): string {
  return `act-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
}

function reducer(state: PerformanceState, action: Action): PerformanceState {
  switch (action.type) {
    case 'ADD_ACTIVITY': {
      const { restaurant_id, activity } = action.payload;
      const newActivity: Activity = {
        ...activity,
        id: generateId(),
        restaurant_id,
        created_at: new Date().toISOString()
      };
      const crmData = state.crmData.map((crm) => {
        if (crm.restaurant_id === restaurant_id) {
          return {
            ...crm,
            activities: [newActivity, ...crm.activities],
            last_contact_date: activity.date
          };
        }
        return crm;
      });
      // If no CRM entry exists yet, create one
      const exists = crmData.some((c) => c.restaurant_id === restaurant_id);
      if (!exists) {
        crmData.push({
          restaurant_id,
          crm_status: 'prospect',
          activities: [newActivity],
          last_contact_date: activity.date,
          assigned_to: null,
          priority: 'medium',
          tags: []
        });
      }
      return { ...state, crmData };
    }

    case 'UPDATE_CRM_STATUS': {
      const { restaurant_id, status } = action.payload;
      const crmData = state.crmData.map((crm) => {
        if (crm.restaurant_id === restaurant_id) {
          return { ...crm, crm_status: status };
        }
        return crm;
      });
      return { ...state, crmData };
    }

    case 'BULK_UPDATE_STATUS': {
      const { restaurant_ids, status } = action.payload;
      const idSet = new Set(restaurant_ids);
      const crmData = state.crmData.map((crm) => {
        if (idSet.has(crm.restaurant_id)) {
          return { ...crm, crm_status: status };
        }
        return crm;
      });
      return { ...state, crmData };
    }

    case 'SET_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.payload } };

    case 'ADD_RESTAURANT': {
      const { restaurant } = action.payload;
      return {
        ...state,
        restaurants: [...state.restaurants, restaurant],
        crmData: [
          ...state.crmData,
          {
            restaurant_id: restaurant.store_id,
            crm_status: 'under_review' as CrmStatus,
            activities: [],
            last_contact_date: null,
            assigned_to: null,
            priority: 'medium',
            tags: ['user_created']
          }
        ]
      };
    }

    case 'HYDRATE':
      return action.payload;

    case 'ADD_LIST': {
      const { list, newRestaurants } = action.payload;
      const newCrmEntries: RestaurantCrmData[] = newRestaurants.map((r) => ({
        restaurant_id: r.store_id,
        crm_status: 'under_review' as CrmStatus,
        activities: [],
        last_contact_date: null,
        assigned_to: null,
        priority: 'medium',
        tags: ['uploaded']
      }));
      return {
        ...state,
        restaurants: [...state.restaurants, ...newRestaurants],
        crmData: [...state.crmData, ...newCrmEntries],
        lists: [...state.lists, list]
      };
    }

    case 'DELETE_LIST': {
      const { listId } = action.payload;
      const newFilters =
        state.filters.activeListId === listId
          ? { ...state.filters, activeListId: null }
          : state.filters;
      return {
        ...state,
        lists: state.lists.filter((l) => l.id !== listId),
        filters: newFilters
      };
    }

    default:
      return state;
  }
}

// ─── Initial state ───

const initialState: PerformanceState = {
  restaurants: RESTAURANTS,
  crmData: INITIAL_CRM_DATA,
  filters: {
    market: MARKETS[0],
    brand: BRANDS[0],
    timePeriod: TIME_PERIODS[0],
    viewMode: 'competitor',
    activeListId: null
  },
  kpis: OVERVIEW_KPIS,
  marketRows: MARKET_ROWS,
  lists: INITIAL_LISTS
};

// ─── Context ───

interface PerformanceContextType {
  state: PerformanceState;
  addActivity: (
    _restaurant_id: number,
    _activity: {
      type: ActivityType;
      date: string;
      notes: string;
      outcome: ActivityOutcome;
      attachment_name?: string;
    }
  ) => void;
  updateCrmStatus: (_restaurant_id: number, _status: CrmStatus) => void;
  bulkUpdateStatus: (_restaurant_ids: number[], _status: CrmStatus) => void;
  setFilters: (_filters: Partial<PerformanceFilters>) => void;
  addRestaurant: (_restaurant: Omit<Restaurant, 'store_id'>) => void;
  addList: (_list: UploadedList, _newRestaurants: Restaurant[]) => void;
  deleteList: (_listId: string) => void;
  getRestaurant: (_id: number) => Restaurant | undefined;
  getCrmData: (_id: number) => RestaurantCrmData | undefined;
  getActiveList: () => UploadedList | undefined;
}

const PerformanceContext = createContext<PerformanceContextType | null>(null);

// ─── Provider ───

export const PerformanceProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [state, dispatch] = useReducer(reducer, initialState, () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge stored CRM data with initial data so new fields (e.g. wine_list_history) are preserved
        const storedCrm: RestaurantCrmData[] = parsed.crmData || [];
        const mergedCrm = initialState.crmData.map((initial) => {
          const saved = storedCrm.find(
            (s: RestaurantCrmData) => s.restaurant_id === initial.restaurant_id
          );
          return saved
            ? {
                ...initial,
                ...saved,
                wine_list_history: initial.wine_list_history
              }
            : initial;
        });
        // Include any stored entries not in initial data (e.g. user-created)
        const initialIds = new Set(
          initialState.crmData.map((c) => c.restaurant_id)
        );
        const extras = storedCrm.filter(
          (s: RestaurantCrmData) => !initialIds.has(s.restaurant_id)
        );
        // Restore user-created restaurants
        const userRestaurants: Restaurant[] = parsed.userRestaurants || [];
        // Restore uploaded lists
        const storedLists: UploadedList[] = parsed.lists || [];
        return {
          ...initialState,
          restaurants: [...initialState.restaurants, ...userRestaurants],
          crmData: [...mergedCrm, ...extras],
          filters: parsed.filters || initialState.filters,
          lists: storedLists.length > 0 ? storedLists : initialState.lists
        };
      }
    } catch {
      // ignore parse errors
    }
    return initialState;
  });

  // Persist to localStorage on state change
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          crmData: state.crmData,
          filters: state.filters,
          userRestaurants: state.restaurants.filter((r) => r.store_id < 0),
          lists: state.lists
        })
      );
    } catch {
      // ignore quota errors
    }
  }, [state.crmData, state.filters, state.restaurants, state.lists]);

  const addRestaurant = useCallback((data: Omit<Restaurant, 'store_id'>) => {
    const tempId = -(Date.now() % 1000000);
    const restaurant: Restaurant = { ...data, store_id: tempId };
    dispatch({ type: 'ADD_RESTAURANT', payload: { restaurant } });
  }, []);

  const addActivity = useCallback(
    (
      restaurant_id: number,
      activity: {
        type: ActivityType;
        date: string;
        notes: string;
        outcome: ActivityOutcome;
        attachment_name?: string;
      }
    ) => {
      dispatch({ type: 'ADD_ACTIVITY', payload: { restaurant_id, activity } });
    },
    []
  );

  const updateCrmStatus = useCallback(
    (restaurant_id: number, status: CrmStatus) => {
      dispatch({
        type: 'UPDATE_CRM_STATUS',
        payload: { restaurant_id, status }
      });
    },
    []
  );

  const bulkUpdateStatus = useCallback(
    (restaurant_ids: number[], status: CrmStatus) => {
      dispatch({
        type: 'BULK_UPDATE_STATUS',
        payload: { restaurant_ids, status }
      });
    },
    []
  );

  const setFilters = useCallback((filters: Partial<PerformanceFilters>) => {
    dispatch({ type: 'SET_FILTERS', payload: filters });
  }, []);

  const getRestaurant = useCallback(
    (id: number) => state.restaurants.find((r) => r.store_id === id),
    [state.restaurants]
  );

  const getCrmData = useCallback(
    (id: number) => state.crmData.find((c) => c.restaurant_id === id),
    [state.crmData]
  );

  const addList = useCallback(
    (list: UploadedList, newRestaurants: Restaurant[]) => {
      dispatch({ type: 'ADD_LIST', payload: { list, newRestaurants } });
    },
    []
  );

  const deleteList = useCallback((listId: string) => {
    dispatch({ type: 'DELETE_LIST', payload: { listId } });
  }, []);

  const getActiveList = useCallback(
    () => state.lists.find((l) => l.id === state.filters.activeListId),
    [state.lists, state.filters.activeListId]
  );

  return (
    <PerformanceContext.Provider
      value={{
        state,
        addActivity,
        updateCrmStatus,
        bulkUpdateStatus,
        setFilters,
        addRestaurant,
        addList,
        deleteList,
        getRestaurant,
        getCrmData,
        getActiveList
      }}
    >
      {children}
    </PerformanceContext.Provider>
  );
};

// ─── Hook ───

export function usePerformance(): PerformanceContextType {
  const ctx = useContext(PerformanceContext);
  if (!ctx) {
    throw new Error('usePerformance must be used within a PerformanceProvider');
  }
  return ctx;
}
